import JSZip from "jszip";
import type { AssignmentResponse } from "../api/types";
import { fetchKretsMembers, type KretsMember } from "../api/kretsMembers";
import { mapboxAccessToken, printMapStyle } from "../map/config";

// Mapbox Static Images API max is 1280px per side; use @2x for doubled output (2560×1812)
const A4_LANDSCAPE_WIDTH = 1280;
const A4_LANDSCAPE_HEIGHT = 906;
const RETINA_SUFFIX = "@2x";
const DEFAULT_PADDING = 80;

type ExportProgress = {
    current: number;
    total: number;
    name: string;
};

type ExportResult = {
    exported: number;
    skipped: number;
};

const slugify = (value: string): string =>
    value
        .trim()
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "")
        .slice(0, 80);

const toPolygonFeatureCollection = (assignment: ExportableAssignment) => {
    const coordinates = assignment.area?.coordinates;
    if (!coordinates || coordinates.length === 0) {
        return null;
    }

    return {
        type: "FeatureCollection",
        features: [
            {
                type: "Feature",
                properties: {
                    assignmentId: assignment.id,
                    assignmentName: assignment.name,
                    stroke: "#114838",
                    "stroke-width": 3,
                    "stroke-opacity": 1,
                    fill: "#4a7a64",
                    "fill-opacity": 0.18
                },
                geometry: {
                    type: "Polygon",
                    coordinates
                }
            }
        ]
    };
};

const createStaticMapUrl = (assignment: ExportableAssignment): string | null => {
    const featureCollection = toPolygonFeatureCollection(assignment);
    if (!featureCollection) {
        return null;
    }

    const overlay = `geojson(${encodeURIComponent(JSON.stringify(featureCollection))})`;
    const styleId = printMapStyle.replace(/^mapbox:\/\/styles\//, "");
    const url = new URL(
        `https://api.mapbox.com/styles/v1/${styleId}/static/${overlay}/auto/${A4_LANDSCAPE_WIDTH}x${A4_LANDSCAPE_HEIGHT}${RETINA_SUFFIX}`
    );

    url.searchParams.set("padding", String(DEFAULT_PADDING));
    url.searchParams.set("logo", "false");
    url.searchParams.set("attribution", "false");
    url.searchParams.set("access_token", mapboxAccessToken);
    return url.toString();
};

const fileName = (assignment: ExportableAssignment, index: number): string => {
    const safeName = slugify(assignment.name) || `assignment-${index + 1}`;
    return `${String(index + 1).padStart(4, "0")}-${safeName}-${assignment.id}.png`;
};

/** No batch index — a single download doesn't need the sort prefix. */
const singleFileName = (assignment: ExportableAssignment): string =>
    `${slugify(assignment.name) || "uppdrag"}-${assignment.id}.png`;

/** Home addresses for the text bar; an unavailable lookup just means no address line. */
const loadKretsMembersById = async (): Promise<Map<string, KretsMember>> => {
    const byId = new Map<string, KretsMember>();
    try {
        const members = await fetchKretsMembers();
        for (const m of members) {
            byId.set(m.id, m);
        }
    } catch {
        // Address lookup unavailable — export continues without addresses.
    }
    return byId;
};

/** Draws text, shortened with an ellipsis if it would not fit within maxWidth. */
const fillTruncated = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number
): void => {
    if (maxWidth <= 0 || ctx.measureText(text).width <= maxWidth) {
        ctx.fillText(text, x, y);
        return;
    }

    let shortened = text;
    while (shortened.length > 1 && ctx.measureText(`${shortened}…`).width > maxWidth) {
        shortened = shortened.slice(0, -1);
    }
    ctx.fillText(`${shortened}…`, x, y);
};

const HEADLINE_FONT = "bold 50px sans-serif";
const DISTRIBUTOR_FONT = "bold 38px sans-serif";
const DISTRIBUTOR_DETAIL_FONT = "38px sans-serif";
const DISTRIBUTOR_LINE_HEIGHT = 46;

/** Kartbild + textrad, ritad på en canvas. Både PNG-export och PDF utgår från den här. */
const renderMapCanvas = (
    imageUrl: string,
    assignment: ExportableAssignment,
    kretsMember: KretsMember | undefined
): Promise<HTMLCanvasElement> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d")!;
            ctx.drawImage(img, 0, 0);

            // Bar: area name (left) | utdelare, adress, telefon (center) | målantal (right).
            // The distributor block is deliberately set smaller than name and count.
            const distributorName =
                kretsMember?.name ?? assignment.kretsMemberName ?? assignment.distributorName ?? null;
            const centerLines = [
                distributorName,
                kretsMember?.homeAddress?.trim() || null,
                kretsMember?.phoneNumber?.trim() || null
            ].filter((line): line is string => !!line);

            const targetLine = assignment.targetAddressCount != null
                ? `Målantal: ${assignment.targetAddressCount}`
                : null;

            const barH = Math.max(90, centerLines.length * DISTRIBUTOR_LINE_HEIGHT + 44);
            const barY = canvas.height - barH;
            const midY = barY + barH / 2;
            const pad = 48;
            const gap = 40;

            ctx.fillStyle = "rgba(255, 255, 255, 0.90)";
            ctx.fillRect(0, barY, canvas.width, barH);

            ctx.fillStyle = "#114838";
            ctx.textBaseline = "middle";

            // Left: area name (bold)
            ctx.font = HEADLINE_FONT;
            ctx.textAlign = "left";
            const nameWidth = ctx.measureText(assignment.name).width;
            ctx.fillText(assignment.name, pad, midY);

            // Right: target count
            let targetWidth = 0;
            if (targetLine) {
                ctx.font = HEADLINE_FONT;
                ctx.textAlign = "right";
                targetWidth = ctx.measureText(targetLine).width;
                ctx.fillText(targetLine, canvas.width - pad, midY);
            }

            // Center: distributor name, home address and phone number, stacked
            if (centerLines.length > 0) {
                const centerX = canvas.width / 2;
                // Keep clear of the headline text on both sides — the block is centred, so the
                // tighter of the two sides decides how wide it may be.
                const available = Math.max(
                    2 * Math.min(centerX - pad - nameWidth - gap, centerX - pad - targetWidth - gap),
                    canvas.width * 0.3
                );
                const firstLineY = midY - ((centerLines.length - 1) * DISTRIBUTOR_LINE_HEIGHT) / 2;

                ctx.textAlign = "center";
                centerLines.forEach((line, index) => {
                    ctx.font = index === 0 ? DISTRIBUTOR_FONT : DISTRIBUTOR_DETAIL_FONT;
                    fillTruncated(ctx, line, centerX, firstLineY + index * DISTRIBUTOR_LINE_HEIGHT, available);
                });
            }

            ctx.textAlign = "left";

            resolve(canvas);
        };
        img.onerror = () => reject(new Error("Kunde inte ladda kartbild från Mapbox"));
        img.src = imageUrl;
    });
};

const renderMapWithTextOverlay = async (
    imageUrl: string,
    assignment: ExportableAssignment,
    kretsMember: KretsMember | undefined
): Promise<Blob> => {
    const canvas = await renderMapCanvas(imageUrl, assignment, kretsMember);
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error("canvas toBlob misslyckades"))),
            "image/png"
        );
    });
};

const triggerDownload = (blob: Blob, fileNameValue: string): void => {
    const href = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.download = fileNameValue;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(href);
};

export const exportAssignmentsAsA4Images = async (
    assignments: AssignmentResponse[],
    onProgress: (progress: ExportProgress) => void
): Promise<ExportResult> => {
    if (!mapboxAccessToken) {
        throw new Error("Mapbox token saknas. Sätt VITE_MAPBOX_TOKEN eller centerreach.mapboxToken.");
    }

    // Pre-fetch krets members so we can look up home addresses by ID.
    const kretsMembersById = await loadKretsMembersById();

    const zip = new JSZip();
    let exported = 0;
    let skipped = 0;

    for (let i = 0; i < assignments.length; i += 1) {
        const assignment = assignments[i];
        onProgress({
            current: i + 1,
            total: assignments.length,
            name: assignment.name
        });

        const url = createStaticMapUrl(assignment);
        if (!url) {
            skipped += 1;
            continue;
        }

        let blob: Blob;
        try {
            const kretsMember = assignment.kretsMemberId
                ? kretsMembersById.get(assignment.kretsMemberId)
                : undefined;
            blob = await renderMapWithTextOverlay(url, assignment, kretsMember);
        } catch (error) {
            console.warn(
                `[export] Mapbox static image failed for "${assignment.name}":`,
                error instanceof Error ? error.message : error
            );
            skipped += 1;
            continue;
        }
        zip.file(fileName(assignment, i), blob);
        exported += 1;
    }

    const archiveBlob = await zip.generateAsync({ type: "blob" });
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    triggerDownload(archiveBlob, `assignments-a4-landscape-${timestamp}.zip`);

    return { exported, skipped };
};

/** Det som behövs för att rita en karta — uppfylls av både AssignmentResponse och SharedAssignment. */
export type ExportableAssignment = Pick<AssignmentResponse, "id" | "name" | "area"> & {
    targetAddressCount?: number;
    kretsMemberId?: string;
    kretsMemberName?: string;
    distributorName?: string;
};

// A4 liggande i millimeter. Marginalen håller textraden innanför skrivarens
// icke-utskrivbara kant — annars kapas utdelarens namn på papperet.
const A4_LANDSCAPE_MM = { width: 297, height: 210 };
const PDF_MARGIN_MM = 5;

/**
 * En A4-sida per uppdrag i ett och samma dokument — gjord för att skrivas ut i en klump.
 * jsPDF laddas först när knappen används; den behövs inte i vanliga kartvyn.
 */
export const exportAssignmentsAsA4Pdf = async (
    assignments: AssignmentResponse[],
    onProgress: (progress: ExportProgress) => void
): Promise<ExportResult> => {
    if (!mapboxAccessToken) {
        throw new Error("Mapbox token saknas. Sätt VITE_MAPBOX_TOKEN eller centerreach.mapboxToken.");
    }

    const { jsPDF } = await import("jspdf");
    const kretsMembersById = await loadKretsMembersById();

    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    let exported = 0;
    let skipped = 0;

    for (let i = 0; i < assignments.length; i += 1) {
        const assignment = assignments[i];
        onProgress({ current: i + 1, total: assignments.length, name: assignment.name });

        const url = createStaticMapUrl(assignment);
        if (!url) {
            skipped += 1;
            continue;
        }

        let canvas: HTMLCanvasElement;
        try {
            const kretsMember = assignment.kretsMemberId
                ? kretsMembersById.get(assignment.kretsMemberId)
                : undefined;
            canvas = await renderMapCanvas(url, assignment, kretsMember);
        } catch (error) {
            console.warn(
                `[export] Mapbox static image failed for "${assignment.name}":`,
                error instanceof Error ? error.message : error
            );
            skipped += 1;
            continue;
        }

        // jsPDF skapar redan sida 1 — nästa sida läggs till först inför bild två.
        if (exported > 0) {
            doc.addPage("a4", "landscape");
        }

        // Bilden behåller sitt bildförhållande och centreras, så inget skalas snett.
        const maxWidth = A4_LANDSCAPE_MM.width - 2 * PDF_MARGIN_MM;
        const maxHeight = A4_LANDSCAPE_MM.height - 2 * PDF_MARGIN_MM;
        const scale = Math.min(maxWidth / canvas.width, maxHeight / canvas.height);
        const drawWidth = canvas.width * scale;
        const drawHeight = canvas.height * scale;

        // JPEG i stället för PNG: en PDF med 20+ PNG-kartor blir tiotals megabyte.
        doc.addImage(
            canvas.toDataURL("image/jpeg", 0.92),
            "JPEG",
            (A4_LANDSCAPE_MM.width - drawWidth) / 2,
            (A4_LANDSCAPE_MM.height - drawHeight) / 2,
            drawWidth,
            drawHeight,
            undefined,
            "FAST"
        );
        exported += 1;
    }

    if (exported === 0) {
        throw new Error("Inget uppdrag hade en ritad yta – ingen PDF skapades.");
    }

    const timestamp = new Date().toISOString().slice(0, 10);
    doc.save(`uppdrag-a4-liggande-${timestamp}.pdf`);

    return { exported, skipped };
};

/**
 * Ritar A4-bilden för ett uppdrag.
 * `includeDistributorDetails: false` hoppar över adress/telefon — används på den öppna
 * utdelarlänken, som varken har eller ska visa de uppgifterna.
 */
const buildAssignmentA4Image = async (
    assignment: ExportableAssignment,
    options?: { includeDistributorDetails?: boolean }
): Promise<Blob> => {
    if (!mapboxAccessToken) {
        throw new Error("Mapbox token saknas. Sätt VITE_MAPBOX_TOKEN eller centerreach.mapboxToken.");
    }

    const url = createStaticMapUrl(assignment);
    if (!url) {
        throw new Error("Uppdraget saknar ritad yta – ingen bild att exportera.");
    }

    const includeDistributorDetails = options?.includeDistributorDetails !== false;
    const kretsMember = includeDistributorDetails && assignment.kretsMemberId
        ? (await loadKretsMembersById()).get(assignment.kretsMemberId)
        : undefined;

    return renderMapWithTextOverlay(url, assignment, kretsMember);
};

/** Single assignment: downloads the PNG directly instead of a one-file zip. */
export const exportAssignmentAsA4Image = async (
    assignment: ExportableAssignment,
    options?: { includeDistributorDetails?: boolean }
): Promise<void> => {
    const blob = await buildAssignmentA4Image(assignment, options);
    triggerDownload(blob, singleFileName(assignment));
};

/**
 * Samma bild, men öppnad i en ny flik. På telefonen hamnar en nedladdad PNG i galleriet
 * utan att visas — där vill man se kartan direkt, och kan spara den därifrån om man vill.
 */
export const openAssignmentA4Image = async (
    assignment: ExportableAssignment,
    options?: { includeDistributorDetails?: boolean }
): Promise<void> => {
    // Fliken måste öppnas synkront i klicket; efter await har popup-blockeraren slagit till.
    const tab = window.open("", "_blank");

    try {
        const blob = await buildAssignmentA4Image(assignment, options);
        if (!tab) {
            // Blockerad popup — bättre att spara bilden än att inte få något alls.
            triggerDownload(blob, singleFileName(assignment));
            return;
        }

        const href = URL.createObjectURL(blob);
        tab.location.href = href;
        // Fliken behöver URL:en kvar en stund; den städas när bilden hunnit laddas.
        window.setTimeout(() => URL.revokeObjectURL(href), 60_000);
    } catch (error) {
        tab?.close();
        throw error;
    }
};

/**
 * Mobil/platta: nedladdning är fel verktyg — bilden ska öppnas i en ny flik i stället.
 * Pekskärm *och* liten skärm, så en touch-laptop fortfarande laddar ned som vanligt.
 */
export const prefersImageInNewTab = (): boolean =>
    window.matchMedia("(pointer: coarse) and (max-width: 900px)").matches;

